import {
  type EmailSignupPreviewConfig,
  type SponsorshipPreviewConfig,
  hasEmailSignupRenderableContent,
} from '@/lib/embed/emailSignupPreview';
import { normalizeCtaImageAspectRatio } from '@/lib/embed/ctaImagePresentation';
import { normalizeValidHttpUrl } from '@/lib/utils/url';

export type EndScreenEmailSignupContent = EmailSignupPreviewConfig;

export interface EndScreenSponsorshipCtaContent {
  cta_tagline: string;
  cta_title: string;
  cta_description: string;
  cta_button_text: string;
  cta_url: string;
  logo_storage_bucket: string | null;
  logo_storage_path: string | null;
  cta_image_storage_bucket: string | null;
  cta_image_storage_path: string | null;
  cta_image_aspect_ratio: number | null;
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function normalizeNullString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeSafeHttpUrl(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '';
  return normalizeValidHttpUrl(value) ?? '';
}

export function normalizeEndScreenEmailSignupContent(
  value: EmailSignupPreviewConfig | null | undefined,
  { allowIncomplete = false }: { allowIncomplete?: boolean } = {}
): EndScreenEmailSignupContent | null {
  if (!value) return null;

  const normalized: EndScreenEmailSignupContent = {
    enabled: Boolean(value.enabled),
    signup_type: value.signup_type === 'embed' ? 'embed' : 'button',
    title: normalizeString(value.title),
    description: normalizeString(value.description),
    button_cta: normalizeString(value.button_cta),
    confirmation_message: normalizeString(value.confirmation_message),
    signup_url: normalizeSafeHttpUrl(value.signup_url),
    embed_code: normalizeString(value.embed_code),
  };

  if (!normalized.enabled) return null;
  return allowIncomplete || hasEmailSignupRenderableContent(normalized) ? normalized : null;
}

export function normalizeEndScreenSponsorshipCtaContent(
  value: SponsorshipPreviewConfig | null | undefined
): EndScreenSponsorshipCtaContent | null {
  if (!value || !value.cta_enabled) return null;

  const normalized: EndScreenSponsorshipCtaContent = {
    cta_tagline: normalizeString(value.cta_tagline),
    cta_title: normalizeString(value.cta_title),
    cta_description: normalizeString(value.cta_description),
    cta_button_text: normalizeString(value.cta_button_text),
    cta_url: normalizeSafeHttpUrl(value.cta_url),
    logo_storage_bucket: normalizeNullString(value.logo_storage_bucket),
    logo_storage_path: normalizeNullString(value.logo_storage_path),
    cta_image_storage_bucket: normalizeNullString(value.cta_image_storage_bucket),
    cta_image_storage_path: normalizeNullString(value.cta_image_storage_path),
    cta_image_aspect_ratio: normalizeCtaImageAspectRatio(value.cta_image_aspect_ratio),
  };

  if (!normalized.cta_url.trim() && !normalized.cta_title.trim()) {
    return null;
  }

  return normalized;
}
